import { Document, Font, Page, StyleSheet, Text, View } from '@react-pdf/renderer';
import { Company } from '../../../../../interfaces/company.interface';

// @ts-expect-error Import
import ManropeRegular from '../../../../../theme/assets/fonts/Manrope-Regular.ttf';
// @ts-expect-error Import
import ManropeMedium from '../../../../../theme/assets/fonts/Manrope-Medium.ttf';
// @ts-expect-error Import
import ManropeBold from '../../../../../theme/assets/fonts/Manrope-Bold.ttf';

import { useTranslation } from 'react-i18next';
import { pdfStyles } from '../../../../../components';
import { getFullDateTime } from '../../../../../config/utils/dateHelpers';
import { Customer } from '../../../../../interfaces/customer.interface';
import { CustomersReportTableHeaderPdf } from './CustomersReportTableHeaderPdf';
import { CustomersReportTableListPdf } from './CustomersReportTableListPdf';

interface Props {
  company: Company;
  customers: Customer[];
  customersTotal?: string;
  suppliersTotal?: string;
}

// Register Font
Font.register({
  family: 'Manrope',
  fonts: [
    {
      src: ManropeRegular,
    },
    {
      src: ManropeMedium,
    },
    {
      src: ManropeBold,
    },
  ],
});

const CHUNK_SIZE = 50; // tune this number (40-100) depending on performance

export const CustomersReportPdf = ({ company, customers, customersTotal = '0.00', suppliersTotal = '0.00' }: Props) => {
  const { t } = useTranslation();

  const pages: Customer[][] = [];
  for (let i = 0; i < (customers?.length || 0); i += CHUNK_SIZE) {
    pages.push(customers.slice(i, i + CHUNK_SIZE));
  }
  if (pages.length === 0) pages.push([]); // ensure at least one page

  return (
    <Document>
      {pages.map((pageCustomers, pageIndex) => {
        const isLastPage = pageIndex === pages.length - 1;
        const startIndex = pageIndex * CHUNK_SIZE;

        return (
          <Page size="A4" style={styles.page} key={pageIndex}>
            <View style={styles.headerPaginator}>
              <Text style={pdfStyles.smallTitle}>{`${pageIndex + 1}/${pages.length}`}</Text>
            </View>

            <View style={[styles.header]}>
              <View style={{ borderBottom: 1, paddingLeft: 4, paddingRight: 4 }}>
                <Text style={pdfStyles.headerTitle}>{t('CUSTOMERS_REPORT.CURRENT_CUSTOMERS_SUBTOTALS')}</Text>
              </View>
              <View style={[styles.columnSection, styles.padding10]}>
                <Text>
                  {company?.title} - {t('CUSTOMERS_REPORT.VAT_NUMBER')}: {company?.vatNumber}
                </Text>
                <View style={{ paddingTop: 6 }}>
                  <Text style={pdfStyles.smallTitle}>{getFullDateTime(new Date().toISOString())}</Text>
                </View>
              </View>

              <View style={{ paddingTop: 4, width: '100%' }}>
                <CustomersReportTableHeaderPdf />
              </View>
            </View>

            {/* Table list for the current page */}
            <View style={{ paddingHorizontal: 16, paddingBottom: 10, paddingTop: 0 }}>
              <CustomersReportTableListPdf customers={pageCustomers} startIndex={startIndex} />
            </View>

            {/* Totals only on the last page */}
            {isLastPage && (
              <View style={{ paddingHorizontal: 20 }}>
                <View style={{ flexDirection: 'row', flexWrap: 'nowrap', paddingTop: 10 }}>
                  <View style={{ flex: 1, flexDirection: 'column', paddingLeft: 4 }}>
                    <Text style={[pdfStyles.smallTitle, pdfStyles.maxLines2]}>{t('CUSTOMERS_REPORT.TOTALS')}</Text>
                  </View>
                  <Text style={[pdfStyles.smallTitle, pdfStyles.width80]}>{customersTotal} EUR</Text>
                  <Text style={[pdfStyles.smallTitle, pdfStyles.width80]}>{suppliersTotal} EUR</Text>
                </View>
              </View>
            )}

            <View style={styles.footer}>
              <Text style={pdfStyles.smallestTitle}>{t('PDF.BY_BILLEASE')}</Text>
            </View>
          </Page>
        );
      })}
    </Document>
  );
};

const styles = StyleSheet.create({
  header: {
    margin: 10,
    marginTop: 0,
    marginBottom: 0,
    padding: 10,
    paddingBottom: 0,
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  padding10: {
    padding: 10,
  },
  columnSection: {
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  separatorLine: {
    width: '100%',
    borderBottomWidth: 1,
  },
  page: {
    fontFamily: 'Manrope',
    fontSize: 12,
    flexDirection: 'column',
    backgroundColor: 'white',
    paddingTop: 12,
    paddingBottom: 30,
    paddingHorizontal: 4,
  },
  footer: {
    position: 'absolute',
    bottom: 12,
    left: 10,
    width: '100%',
  },
  headerPaginator: {
    position: 'absolute',
    top: 12,
    left: 10,
    width: '100%',
  },
});
